/**
 * SIM Service for handling SIM card operations
 *
 * SECURITY UPDATE: April 2025
 * All direct references to backend URLs have been replaced with secure server proxies
 * to prevent exposing sensitive backend URLs in client-side code.
 */
export const useSimService = () => {
  /**
   * Verify a SIM card
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const verifySim = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/verify-sim`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });
      return response?.result || null;
    } catch (error: any) {
      if (error.response?._data?.message) {
        const friendlyError = new Error(error.response._data.message);
        friendlyError.name = "SimValidationError";
        throw friendlyError;
      } else if (error.response?._data?.error) {
        throw error.response._data.error;
      }
      throw error;
    }
  };

  /**
   * Activate a SIM card
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const activateSim = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/activate-sim`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Proceed with SIM card activation flow
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const proceedSim = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/proceed-sim`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Verify address during SIM activation
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const activateSimVerifyAddress = async (
    payload: object
  ): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/activate-sim/verify-address`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  /**
   * Verify payment during SIM activation
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const activateSimVerifyPayment = async (
    payload: object
  ): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/activate-sim/verify-payment`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Verify DVS GreenId during SIM activation
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const activateSimDvsGreenIdClient = async (
    payload: object
  ): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/activate-sim/dvs-green-id-client`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  return {
    verifySim,
    activateSim,
    proceedSim,
    activateSimVerifyAddress,
    activateSimVerifyPayment,
    activateSimDvsGreenIdClient,
  };
};
